import styled, { css, useTheme } from "styled-components";
import AntSelect, {
  BaseOptionType,
  DefaultOptionType,
  SelectProps as AntSelectProps,
} from "antd/es/select";
import { SelectValue } from "antd/es/select";
import { FixMeLater } from "types";
import { ReactElement, useEffect, useState } from "react";
import { hexToRGB } from "@shared/utils/misc";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { useBottomNavigator } from "utils/hooks/misc";
import MobileSelect from "common/Select/MobileSelect";
import CloseCircleFilled from "@ant-design/icons/CloseCircleFilled";
import {
  NEW_LOOK_CHECK_ICON,
  NEW_LOOK_X_ICON,
  newLookMaskIcon,
} from "common/Inputs/styles";

export interface SelectProps<
  VT,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
> extends AntSelectProps<VT, OptionType> {
  flushPaddingLeft?: boolean;
  error?: boolean;
  rounded?: boolean;
  alwaysShowClear?: boolean;
  hideArrow?: boolean;
  alignRight?: boolean;
  mobileTitle?: string;
  triggerElement?: ReactElement;
  bottomExtraElement?: React.JSX.Element;
  titleRightElement?: React.JSX.Element;
  zIndex?: number;
}

const Select = <
  VT extends SelectValue = SelectValue,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
>({
  flushPaddingLeft,
  error,
  styles,
  rounded,
  alwaysShowClear,
  hideArrow,
  alignRight,
  mobileTitle,
  open: propsOpen,
  onOpenChange: propsOnOpenChange,
  triggerElement,
  bordered,
  variant,
  bottomExtraElement,
  titleRightElement,
  zIndex,
  ...props
}: SelectProps<VT, OptionType>) => {
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [mobileOpen, setMobileOpen] = useState(propsOpen);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMobileSelect = isBottomNavigator && !!props.options;
  const open = propsOpen ?? (isMobileSelect ? mobileOpen : undefined);
  const allowClear =
    typeof props.allowClear === "boolean" && props.allowClear
      ? { clearIcon: <ClearIcon /> }
      : props.allowClear;

  useEffect(() => {
    if (isMobileSelect) {
      setMobileOpen(propsOpen);
    }
  }, [propsOpen]);

  const onOpenChange = (open: boolean) => {
    propsOnOpenChange?.(open);
    if (isMobileSelect) {
      setMobileOpen(open);
    }
  };

  const updatedStyles = {
    ...styles,
    popup: {
      ...styles?.popup,
      root: {
        ...styles?.popup?.root,
        background: appTheme.colors.cardBgColor,
      },
    },
  };

  const getNonMobileShowSearch = () => {
    if (isMobileSelect) return false;
    if (props.showSearch !== undefined) return props.showSearch;
    return !isMobile || (props.options?.length || 0) > 30 ? {} : false;
  };

  return (
    <>
      {isMobileSelect && (
        <MobileSelect
          open={open}
          onOpenChange={onOpenChange}
          mobileTitle={mobileTitle}
          bottomExtraElement={bottomExtraElement}
          titleRightElement={titleRightElement}
          zIndex={zIndex}
          {...props}
        />
      )}
      {triggerElement ?? (
        <StyledSelect
          open={isMobileSelect ? false : open}
          onOpenChange={onOpenChange}
          $flushPaddingLeft={flushPaddingLeft}
          $error={error}
          $rounded={rounded}
          $alwaysShowClear={alwaysShowClear}
          $hideArrow={hideArrow}
          $alignRight={alignRight}
          variant={variant ?? (bordered === false ? "borderless" : undefined)}
          {...props}
          showSearch={getNonMobileShowSearch()}
          styles={updatedStyles}
          classNames={{
            popup: { root: appTheme.isDarkMode ? "dark-mode" : "" },
          }}
          allowClear={allowClear}
        />
      )}
    </>
  );
};

Select.Option = AntSelect.Option;
Select.OptGroup = AntSelect.OptGroup;

export default Select;

export const selectStyle = css`
  .ant-select-suffix {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }

  .ant-select-dropdown.dark-mode {
    ::-webkit-scrollbar {
      width: 15px;
    }

    ::-webkit-scrollbar-track {
      background-color: transparent;
    }

    ::-webkit-scrollbar-thumb {
      border: 4px solid transparent;
      background-color: ${(p) => hexToRGB(p.theme.colors.secondaryGrey, 0.8)};
      border-radius: 100px;
      background-clip: content-box;
    }
  }

  &.ant-select.ant-select-disabled.ant-select:not(.ant-select-customize-input) {
    opacity: 0.5;
  }

  &.ant-select-multiple .ant-select-selection-item {
    border-color: ${(p) => p.theme.colors.lightBorderGrey};

    .anticon-close {
      color: ${(p) => p.theme.colors.basic};
    }
  }

  ${(p) => p.theme.isNewLook && newLookSelectStyle}
`;

// New look: quiet filled chips for multi-select values, and roomy option rows
// with a check mark on the selected option.
const newLookSelectStyle = css`
  /* Disabled selects read as disabled, like disabled inputs beside them. */
  &.ant-select-disabled .ant-select-selection-item,
  &.ant-select-disabled .ant-select-selection-placeholder,
  .ant-select-disabled .ant-select-selection-item,
  .ant-select-disabled .ant-select-selection-placeholder {
    color: ${(p) => p.theme.colors.lightestText} !important;
  }
  .ant-select-multiple .ant-select-selection-item {
    background: ${(p) => p.theme.colors.cardLightBgColor};
    border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
    border-radius: 6px;
    color: ${(p) => p.theme.colors.primaryText};
    font-size: 13px;
    padding-inline: 8px 4px;

    .ant-select-selection-item-remove {
      display: inline-flex;
      margin-inline-start: 2px;
      color: ${(p) => p.theme.colors.secondaryText};
      border-radius: 4px;
      &:hover {
        color: ${(p) => p.theme.colors.primaryText};
        background: ${(p) => p.theme.colors.lightBorderGrey};
      }
      .anticon-close {
        color: inherit;
        padding: 2px;
        ${newLookMaskIcon(NEW_LOOK_X_ICON, 12)};
      }
    }
  }

  .ant-select-dropdown {
    padding: 6px;
    border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
    border-radius: 12px;
    box-shadow: ${(p) => p.theme.shadows.hoverCard};

    .rc-virtual-list:has(.rc-virtual-list-scrollbar-vertical)
      .rc-virtual-list-holder-inner {
      padding-inline-end: 16px;
      box-sizing: border-box;
    }

    .ant-select-item {
      min-height: 34px;
      padding: 7px 10px;
      border-radius: 8px;
      align-items: center;
      font-weight: 400;
      color: ${(p) => p.theme.colors.primaryText};
    }
    .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
      background: ${(p) => p.theme.colors.cardLightBgColor};
    }
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
      background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.08)};
      color: ${(p) => p.theme.colors.primaryBlue};
      font-weight: 500;
    }
    .ant-select-item-option-state {
      color: ${(p) => p.theme.colors.primaryBlue};
    }
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled)
      .ant-select-item-option-state:empty::before {
      content: "";
      display: block;
      width: 14px;
      height: 14px;
      margin-inline-start: 8px;
      background-color: currentColor;
      -webkit-mask: ${NEW_LOOK_CHECK_ICON} center / contain no-repeat;
      mask: ${NEW_LOOK_CHECK_ICON} center / contain no-repeat;
    }
    .ant-select-item-group {
      min-height: 28px;
      padding: 10px 10px 4px;
      font-size: 12px;
      font-weight: 500;
      color: ${(p) => p.theme.colors.secondaryText};
    }
  }
`;

const StyledSelect = styled(AntSelect)<FixMeLater>`
  ${(props) =>
    props.$flushPaddingLeft &&
    `
    .ant-select-selector {
      padding-left: 0 !important;
    }
  `}
  ${(props) =>
    props.$error &&
    `
    .ant-select-selection-item {
      color: ${props.theme.colors.red};
    }
  `}
  ${(p) =>
    p.$rounded &&
    `
    .ant-select-selector {
      border-radius: 8px !important;
    }
  `};
  ${(p) =>
    p.$alwaysShowClear &&
    `
     .ant-select-clear {
      opacity: 1
     }
  `};
  ${(p) =>
    p.$hideArrow &&
    `
     .ant-select-arrow {
        display: none;
     }
  `};
  ${(p) =>
    p.$alignRight &&
    `
     .ant-select-selection-placeholder{
         right: 0;
     }
  `};
  ${selectStyle};
`;
const ClearIcon = styled(CloseCircleFilled)`
  padding: 8px;
  margin: -8px;
`;
